import { useSnackbar } from 'notistack';
import type { OptionsObject, SnackbarOrigin } from 'notistack';
import Button from '@mui/material/Button';
import { useCallback, useMemo } from 'react';

type Options = {
  anchorOrigin: SnackbarOrigin;
  autoHideDuration: number | null;
};

const useMessageSnackbar = (customOptions?: Options) => {
  const { enqueueSnackbar } = useSnackbar();

  const memoizedOptions = useMemo(() => {
    if (customOptions) {
      return customOptions;
    }

    return {
      anchorOrigin: { vertical: 'top', horizontal: 'center' },
      autoHideDuration: 4000,
    } as OptionsObject;
  }, [customOptions]);

  const displayErrorSnackbar = useCallback(
    (message: string) => {
      enqueueSnackbar(message, {
        variant: 'error',
        ...memoizedOptions,
      });
    },
    [memoizedOptions, enqueueSnackbar]
  );

  const displaySuccessSnackbar = useCallback(
    (message: string, onClickText: string, onClickAction?: () => void) => {
      const actionButton = () => (
        <Button variant="textDark" onClick={onClickAction}>
          {onClickText}
        </Button>
      );
      const action = onClickAction ? () => actionButton() : null;
      enqueueSnackbar(message, {
        variant: 'success',
        onClick: onClickAction,
        action,
        ...memoizedOptions,
      });
    },
    [memoizedOptions, enqueueSnackbar]
  );

  return {
    displayErrorSnackbar,
    displaySuccessSnackbar,
  };
};

export default useMessageSnackbar;
